import { supabase } from '../lib/supabaseClient';
import { cachedFetch, invalidateCache } from './cache';
import type { Menu, MenuPrice, UUID } from '../types/domain';

/**
 * Menu master adapter (FASE 7, migration 0039). menus/menu_prices are
 * tenant-wide (no branch_id) — see docs/data-dictionary.md NEEDS_DECISION
 * log, 2026-08-19. Every write goes through a permission-gated RPC
 * (fn_create_menu / fn_update_menu / fn_set_menu_price), never a direct
 * `.insert()`/`.update()` — same convention as api/purchasing.ts.
 */

export async function listMenus(): Promise<Menu[]> {
  return cachedFetch('menus', async () => {
    const { data, error } = await supabase
      .from('menus')
      .select('menu_id, tenant_id, menu_code, name, status, created_at, updated_at')
      .order('name', { ascending: true });
    if (error) throw new Error(`listMenus failed: ${error.message}`);
    return (data ?? []) as Menu[];
  });
}

export async function listMenuPrices(menuId: UUID): Promise<MenuPrice[]> {
  const { data, error } = await supabase
    .from('menu_prices')
    .select('menu_price_id, tenant_id, menu_id, price, effective_from, effective_to, created_at')
    .eq('menu_id', menuId)
    .order('effective_from', { ascending: false });
  if (error) throw new Error(`listMenuPrices failed: ${error.message}`);
  return (data ?? []) as MenuPrice[];
}

/** Calls fn_create_menu (migration 0039, permission-gated on menu.create). */
export async function createMenu(menuCode: string, name: string): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_menu', { p_menu_code: menuCode, p_name: name });
  if (error) throw new Error(`fn_create_menu failed: ${error.message}`);
  invalidateCache('menus');
  return data as UUID;
}

/** Calls fn_update_menu (migration 0039, permission-gated on menu.create — the same action covers create/edit, matching item.create's convention). */
export async function updateMenu(menuId: UUID, name: string, status: 'ACTIVE' | 'INACTIVE'): Promise<void> {
  const { error } = await supabase.rpc('fn_update_menu', { p_menu_id: menuId, p_name: name, p_status: status });
  if (error) throw new Error(`fn_update_menu failed: ${error.message}`);
  invalidateCache('menus');
}

/** Calls fn_set_menu_price (migration 0039) — closes the currently-open price row and inserts a new date-effective one. */
export async function setMenuPrice(menuId: UUID, price: number, effectiveFrom: string): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_set_menu_price', {
    p_menu_id: menuId,
    p_price: price,
    p_effective_from: effectiveFrom,
  });
  if (error) throw new Error(`fn_set_menu_price failed: ${error.message}`);
  return data as UUID;
}
